import React, { useEffect, useState } from "react";
import { JobWithShare } from "@/lib/supabase/supabaseTypes";
import { useNavigate } from "react-router-dom";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { SheetContentProps } from "@/dentlab/src/store/Sheets/sheet-provider/sheetRegistry";
import { useSheetStore } from "../../sheet.store";
import {
	getStatusColor,
	getStatusIcon,
} from "@/dentlab/src/lib/utils/job-utils";
import { JobItemsPreviewTable } from "./components/JobItemsPreviewTable";
import { JobSheetDeliveries } from "./components/JobSheetDeliveries";
import {
	Collapsible,
	CollapsibleContent,
} from "@/dentlab/src/components/ui/collapsible";
import { JobDocumentTags } from "@/dentlab/src/pages/job-page/job-document/job-document-tag.component";
import { ChatComponent } from "@/dentlab/src/components/chat/chat.component";
import { AcceptJobRequestResult } from "@/dentlab/src/store/Jobs/job.store";

export interface JobSheetContentProps {
	job: JobWithShare | null;
	onShareFile: (fileId: string) => Promise<void>;
	onUnshareFile: (fileId: string) => Promise<void>;
	onAcceptJobRequest: (
		channelId: string | null
	) => Promise<AcceptJobRequestResult>;
	onDenyJobRequest: (channelId: string | null) => Promise<boolean>;
}

export const JobSheetContent: React.FC<
	JobSheetContentProps & SheetContentProps
> = ({
	sheetKey,
	job,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
}) => {
	const navigate = useNavigate();
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const closeSheet = useSheetStore((state) => state.closeSheet);

	const { patientsLookup, fetchPatientsByIds, patientName } = usePatientStore(
		(state) => ({
			patientsLookup: state.patientsLookup,
			fetchPatientsByIds: state.fetchPatientsByIds,
			patientName: state.patientName,
		})
	);

	const [isChatOpen, setIsChatOpen] = useState(true);

	useEffect(() => {
		if (job?.patient_id) {
			fetchPatientsByIds([job.patient_id]);
		}
	}, [job?.patient_id]);

	const getPatientInfoById = (patientId: string | null) => {
		if (!patientId || !patientsLookup) return "Unbekannt";
		const patient = patientsLookup[patientId];
		if (!patient) return "Unbekannt";
		const name = patientName(patient);
		const birthDate = (patient as any).birth_date
			? `(${new Date((patient as any).birth_date).toLocaleDateString(
					"de-DE"
				)})`
			: "";
		return `${name} ${birthDate}`.trim() || "Unbekannt";
	};

	if (!job) return null;

	const formatDate = (dateString: string | null) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleDateString("de-DE", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		});
	};

	const handleNavigateToJob = () => {
		if (job.client_id && job.id) {
			navigate(`/client/${job.client_id}/job/${job.id}`);
			closeSheet(sheetKey);
		}
	};

	return (
		<div className="p-4">
			<div className="pb-2">
				<div className="flex items-center justify-between gap-2 text-xl pb-2">
					<div className="flex items-center gap-2">
						<span
							className={cn(
								"p-1 rounded-full",
								getStatusColor(job.status as JobStatusEnum)
							)}
						>
							{getStatusIcon(job.status as JobStatusEnum)}
						</span>
						<span className="truncate">{job.title}</span>
					</div>
					<JobDocumentTags job={job} />
				</div>
				<div className="flex items-center justify-between">
					<div className="flex flex-col gap-2">
						<span className="font-mono">
							Code: {job.code || "-"}
						</span>
						{job.organization_id === organization?.id &&
							job.patient_id && (
								<span className="font-mono">
									Patient:{" "}
									{getPatientInfoById(job.patient_id)}
								</span>
							)}
					</div>
					<span className="text-sm font-medium px-2 py-1 rounded-full bg-gray-100 border">
						{job.status !== null &&
							jobStatusToLabel[job.status as JobStatusEnum]}
					</span>
				</div>
			</div>
			<JobItemsPreviewTable job={job} />
			{job.id && <JobSheetDeliveries key={job.id} jobId={job.id} />}
			{job.shared_with && (
				<Collapsible
					className="mb-4"
					onOpenChange={setIsChatOpen}
					open={isChatOpen}
				>
					{/* 					<CollapsibleTriggerStandard
						open={isChatOpen}
						icon={<MessageCircle className="h-4 w-4" />}
					>
						{messageCount > 0
							? `Chat mit ${messageCount} ${messageCount === 1 ? "Nachricht" : "Nachrichten"}`
							: "Chat"}
					</CollapsibleTriggerStandard> */}
					<CollapsibleContent>
						<div className="h-[calc(60vh)] mb-4">
							{job.id && job.shared_with && organization?.id ? (
								<ChatComponent
									channelId={job.channel_id ?? null}
									organizationId={organization.id}
									onShareFile={onShareFile}
									onUnshareFile={onUnshareFile}
									onAcceptJobRequest={onAcceptJobRequest}
									onDenyJobRequest={onDenyJobRequest}
									allowGoTo={false}
									checkForClientConnectionToCustomer={false}
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<p className="text-sm text-gray-500">
										Dieser Auftrag ist nicht geteilt. Teilen
										Sie ihn, um zu chatten.
									</p>
								</div>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>
			)}
		</div>
	);
};
